import { useTranslation } from 'react-i18next';

import PropTypes from 'prop-types';

import { Skeleton } from '../../../components/ui/skeleton';
import { COLORS } from './dataPlotConstants';

const SKELETON_ROW_COUNT = 7;

function RecentUploadsTable({
    tableRows,
    columnWidths,
    keys,
    isMobile,
    isLoading,
    titleWithTopMargin,
    buildCardStyle,
}: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'Overview' });
    return (
        <div className='card' style={buildCardStyle('large', { alignItems: 'stretch' })}>
            <div style={titleWithTopMargin}>{t('recentUpload')}</div>
            <div
                style={{
                    width: '100%',
                    overflowX: isMobile ? 'hidden' : 'auto',
                    padding: isMobile ? '0 12px' : '0 20px',
                }}
            >
                <div
                    style={{
                        // Fixed rather than max height: the row count varies with the range (and with
                        // the skeleton), and a content-sized box would shift everything below it.
                        // Desktop already reserves a fixed 300px card for the same reason.
                        height: isMobile ? '240px' : 'auto',
                        overflowY: isMobile ? 'auto' : 'visible',
                    }}
                >
                    <table
                        style={{
                            width: '100%',
                            // Trimmed from 480px to stay inside the padding above at the
                            // card's 500px minimum width, so no scrollbar appears there.
                            minWidth: isMobile ? '100%' : '440px',
                            tableLayout: isMobile ? 'fixed' : 'auto',
                            borderCollapse: 'separate',
                            borderSpacing: '0px 5px',
                            margin: '0 auto',
                        }}
                    >
                        <thead
                            style={
                                isMobile
                                    ? {
                                          position: 'sticky',
                                          top: 0,
                                          backgroundColor: COLORS.white,
                                          zIndex: 2,
                                      }
                                    : undefined
                            }
                        >
                            <tr>
                                <th
                                    style={{
                                        textAlign: 'left',
                                        borderBottom: `1px solid ${COLORS.border}`,
                                        padding: '8px 0',
                                        width: columnWidths.time,
                                    }}
                                >
                                    {t('time')}
                                </th>
                                <th
                                    style={{
                                        textAlign: 'left',
                                        borderBottom: `1px solid ${COLORS.border}`,
                                        padding: '8px 0',
                                        width: columnWidths.region,
                                    }}
                                >
                                    {t('region')}
                                </th>
                                <th
                                    style={{
                                        textAlign: 'left',
                                        borderBottom: `1px solid ${COLORS.border}`,
                                        padding: '8px 0',
                                        width: columnWidths.company,
                                    }}
                                >
                                    {t('company')}
                                </th>
                                <th
                                    style={{
                                        textAlign: 'left',
                                        borderBottom: `1px solid ${COLORS.border}`,
                                        padding: '8px 0',
                                        width: columnWidths.status,
                                    }}
                                >
                                    {t('status')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIndex: number) => (
                                    <tr key={`skeleton-row-${rowIndex}`}>
                                        {keys.map((key: any) => (
                                            <td
                                                key={`skeleton-${rowIndex}-${key}`}
                                                style={{ padding: '8px 0', width: columnWidths[key] }}
                                            >
                                                {key === 'status' ? (
                                                    <Skeleton className='h-[28px] w-[70px] rounded-[10px]' />
                                                ) : (
                                                    <Skeleton className='h-4 w-4/5' />
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : tableRows.length ? (
                                tableRows.map((data: any) => (
                                    <tr key={data.time}>
                                        {keys.map((key: any) => {
                                            if (key === 'status') {
                                                const isPass = data[key] === true;
                                                const statusColors = isPass
                                                    ? { bg: '#3F8EE829', text: '#3F8EE8', label: t('pass') }
                                                    : { bg: '#FF383829', text: '#FF3838', label: t('fail') };
                                                return (
                                                    <td key={`${data.time}-${key}`} style={{ padding: '8px 0' }}>
                                                        <div
                                                            style={{
                                                                width: '70px',
                                                                fontSize: '16px',
                                                                padding: '3px',
                                                                textAlign: 'center',
                                                                backgroundColor: statusColors.bg,
                                                                borderRadius: '10px',
                                                                color: statusColors.text,
                                                            }}
                                                        >
                                                            {statusColors.label}
                                                        </div>
                                                    </td>
                                                );
                                            }
                                            return (
                                                <td
                                                    key={`${data.time}-${key}`}
                                                    style={{
                                                        color: COLORS.textSecondary,
                                                        padding: '8px 0',
                                                        width: columnWidths[key],
                                                    }}
                                                >
                                                    {data[key]}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))
                            ) : (
                                <tr />
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

RecentUploadsTable.propTypes = {
    tableRows: PropTypes.array.isRequired,
    columnWidths: PropTypes.object.isRequired,
    keys: PropTypes.array.isRequired,
    isMobile: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool,
    titleWithTopMargin: PropTypes.object.isRequired,
    buildCardStyle: PropTypes.func.isRequired,
};

export default RecentUploadsTable;
